import { FUNCTION_BASE } from "../lib/constants";
import { authFetch } from "../lib/api";
import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../lib/firebase";
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { FaUsers, FaEllipsisH } from "react-icons/fa";
import "../styles/Audiences.css";

const genderOptions = ["All Genders", "Male", "Female"];

export default function AudiencesPage() {
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [openMenuId, setOpenMenuId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editingAudience, setEditingAudience] = useState(null); // null = create, object = edit

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(65);
  const [gender, setGender] = useState("All Genders");

  const [locations, setLocations] = useState([]);
  const [locationInput, setLocationInput] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [searchingLocations, setSearchingLocations] = useState(false);
  const locationTimer = useRef(null);

  const [interests, setInterests] = useState([]);
  const [interestInput, setInterestInput] = useState("");
  const [interestResults, setInterestResults] = useState([]);
  const [searchingInterests, setSearchingInterests] = useState(false);
  const interestTimer = useRef(null);

  useEffect(() => { fetchAudiences(); }, []);

  useEffect(() => {
    const handler = () => setOpenMenuId(null);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchAudiences = async () => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "users", user.uid, "savedAudiences"));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setAudiences(list);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openCreateModal = () => {
    setEditingAudience(null);
    setName(""); setDescription(""); setAgeMin(18); setAgeMax(65);
    setGender("All Genders"); setLocations([]); setLocationInput(""); setLocationResults([]);
    setInterests([]); setInterestInput(""); setInterestResults([]);
    setCreateError("");
    setShowModal(true);
  };

  const openEditModal = (aud) => {
    setEditingAudience(aud);
    setName(aud.name || "");
    setDescription(aud.description || "");
    setAgeMin(aud.ageMin ?? 18);
    setAgeMax(aud.ageMax ?? 65);
    setGender(aud.gender || "All Genders");
    setLocations(aud.locations || []);
    setInterests(aud.interests || []);
    setLocationInput(""); setLocationResults([]);
    setInterestInput(""); setInterestResults([]);
    setCreateError("");
    setShowModal(true);
  };

  const handleLocationInput = (val) => {
    setLocationInput(val);
    if (locationTimer.current) clearTimeout(locationTimer.current);
    if (!val.trim()) { setLocationResults([]); return; }
    setSearchingLocations(true);
    locationTimer.current = setTimeout(async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const params = new URLSearchParams({ userId: user.uid, q: val });
        const res = await authFetch(`${FUNCTION_BASE}/api/fb/location-search?${params}`);
        const data = await res.json();
        if (data.success) setLocationResults(data.locations || []);
      } catch (e) { console.error(e); }
      finally { setSearchingLocations(false); }
    }, 350);
  };

  const addLocation = (loc) => {
    if (!locations.find(l => l.key === loc.key)) setLocations(prev => [...prev, loc]);
    setLocationInput("");
    setLocationResults([]);
  };

  const handleInterestInput = (val) => {
    setInterestInput(val);
    if (interestTimer.current) clearTimeout(interestTimer.current);
    if (!val.trim()) { setInterestResults([]); return; }
    setSearchingInterests(true);
    interestTimer.current = setTimeout(async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const params = new URLSearchParams({ userId: user.uid, q: val });
        const res = await authFetch(`${FUNCTION_BASE}/api/fb/interest-search?${params}`);
        const data = await res.json();
        if (data.success) setInterestResults(data.interests || []);
      } catch (e) { console.error(e); }
      finally { setSearchingInterests(false); }
    }, 350);
  };

  const addInterest = (interest) => {
    if (!interests.find(i => i.id === interest.id)) setInterests(prev => [...prev, interest]);
    setInterestInput("");
    setInterestResults([]);
  };

  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) return;
    if (!name.trim()) { setCreateError("Please enter an audience name."); return; }
    if (ageMin >= ageMax) { setCreateError("Min age must be less than max age."); return; }
    setCreating(true); setCreateError("");
    try {
      const data = {
        name: name.trim(), description: description.trim(),
        ageMin: Number(ageMin), ageMax: Number(ageMax),
        gender, locations, interests,
      };
      if (editingAudience) {
        await updateDoc(doc(db, "users", user.uid, "savedAudiences", editingAudience.id), data);
      } else {
        await addDoc(collection(db, "users", user.uid, "savedAudiences"), {
          ...data, createdAt: serverTimestamp(),
        });
      }
      await fetchAudiences();
      setShowModal(false);
    } catch (e) { setCreateError("Failed to save: " + e.message); }
    setCreating(false);
  };

  const handleCopy = async (aud) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await addDoc(collection(db, "users", user.uid, "savedAudiences"), {
        name: `Copy of ${aud.name}`,
        description: aud.description || "",
        ageMin: aud.ageMin, ageMax: aud.ageMax,
        gender: aud.gender, locations: aud.locations || [], interests: aud.interests || [],
        createdAt: serverTimestamp(),
      });
      await fetchAudiences();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (audienceId) => {
    const user = auth.currentUser;
    if (!user) return;
    if (!window.confirm("Delete this audience?")) return;
    await deleteDoc(doc(db, "users", user.uid, "savedAudiences", audienceId));
    setAudiences(prev => prev.filter(a => a.id !== audienceId));
  };

  const filtered = audiences.filter(a =>
    (a.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="modal-card">
            <div className="modal-header">
              <div>
                <h2 className="modal-title">{editingAudience ? "Edit Audience" : "Create Saved Audience"}</h2>
                <p className="modal-subtitle">Save targeting criteria to reuse across campaigns</p>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {/* Name */}
              <div className="aud-field">
                <label className="aud-label">Audience Name *</label>
                <input className="aud-input" type="text" value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. US Young Adults 18-35" />
              </div>

              {/* Description */}
              <div className="aud-field">
                <label className="aud-label">Description <span className="aud-label-optional">(optional)</span></label>
                <input className="aud-input" type="text" value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Add a note about this audience..." />
              </div>

              {/* Age Range */}
              <div className="aud-field">
                <label className="aud-label">Age Range</label>
                <div className="age-row">
                  <input className="aud-input aud-input-sm" type="number" min={13} max={65}
                    value={ageMin} onChange={e => setAgeMin(Number(e.target.value))} />
                  <span className="age-sep">to</span>
                  <input className="aud-input aud-input-sm" type="number" min={13} max={65}
                    value={ageMax} onChange={e => setAgeMax(Number(e.target.value))} />
                </div>
              </div>

              {/* Gender */}
              <div className="aud-field">
                <label className="aud-label">Gender</label>
                <div className="gender-row">
                  {genderOptions.map(g => (
                    <button key={g} type="button"
                      className={`gender-btn${gender === g ? " active" : ""}`}
                      onClick={() => setGender(g)}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              {/* Locations */}
              <div className="aud-field">
                <label className="aud-label">Locations</label>
                <div className="search-field">
                  <input className="aud-input" type="text" value={locationInput}
                    onChange={e => handleLocationInput(e.target.value)}
                    placeholder="Search cities, states, countries..."
                    autoComplete="off" />
                  {(locationResults.length > 0 || searchingLocations) && (
                    <div className="search-dropdown">
                      {searchingLocations && <div className="search-dropdown-searching">Searching...</div>}
                      {locationResults.map(loc => (
                        <div key={loc.key} className="search-dropdown-item" onMouseDown={() => addLocation(loc)}>
                          <span className="search-dropdown-label">{loc.name}{loc.region ? `, ${loc.region}` : ""}</span>
                          <span className="search-dropdown-meta">{loc.type}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {locations.length > 0 && (
                  <div className="tags-row">
                    {locations.map(loc => (
                      <span key={typeof loc === "object" ? loc.key : loc} className="form-tag form-tag-blue">
                        {typeof loc === "object" ? loc.name : loc}
                        {loc.region ? `, ${loc.region}` : ""}
                        <span className="form-tag-remove" onClick={() => setLocations(prev => prev.filter(l => l.key !== loc.key))}>×</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Interests */}
              <div className="aud-field">
                <label className="aud-label">Interests & Behaviors</label>
                <div className="search-field">
                  <input className="aud-input" type="text" value={interestInput}
                    onChange={e => handleInterestInput(e.target.value)}
                    placeholder="Search interests, e.g. Fitness, Travel..."
                    autoComplete="off" />
                  {(interestResults.length > 0 || searchingInterests) && (
                    <div className="search-dropdown">
                      {searchingInterests && <div className="search-dropdown-searching">Searching...</div>}
                      {interestResults.map(interest => (
                        <div key={interest.id} className="search-dropdown-item" onMouseDown={() => addInterest(interest)}>
                          <span className="search-dropdown-label">{interest.name}</span>
                          {interest.audience_size && (
                            <span className="search-dropdown-meta">{(interest.audience_size / 1e6).toFixed(1)}M</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {interests.length > 0 && (
                  <div className="tags-row">
                    {interests.map(int => (
                      <span key={int.id} className="form-tag form-tag-purple">
                        {int.name}
                        <span className="form-tag-remove" onClick={() => setInterests(prev => prev.filter(i => i.id !== int.id))}>×</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {createError && <div className="aud-error">{createError}</div>}

              <div className="modal-footer">
                <button className="aud-cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="aud-save-btn" onClick={handleSave} disabled={creating}>
                  {creating ? "Saving..." : editingAudience ? "Save Changes" : "Save Audience"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="aud-header">
        <div>
          <h1 className="aud-title">Audiences</h1>
          <p className="aud-subtitle">Save targeting presets to reuse across your campaigns</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="aud-toolbar">
        <button className="aud-new-btn" onClick={openCreateModal}>+ New Audience</button>
        <input className="aud-search-input" type="text" placeholder="Search audiences..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* List */}
      {loading ? (
        <div className="aud-loading">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="aud-empty">
          <div className="aud-empty-icon"><FaUsers /></div>
          <div className="aud-empty-title">No saved audiences yet</div>
          <p className="aud-empty-desc">
            Save your targeting criteria as reusable presets — age, gender, locations, and interests.
            Select a saved audience in the campaign wizard to auto-fill your targeting in seconds.
          </p>
          <button className="aud-empty-btn" onClick={openCreateModal}>+ Create First Audience</button>
        </div>
      ) : (
        <div className="aud-grid">
          {filtered.map(aud => (
            <div key={aud.id} className="aud-card">
              <div className="aud-card-header">
                <div className="aud-card-icon"><FaUsers /></div>
                <div className="aud-card-title-wrap">
                  <div className="aud-card-name">{aud.name}</div>
                  {aud.description && <div className="aud-card-desc">{aud.description}</div>}
                </div>
                <div className="aud-card-menu-wrap">
                  <button className="aud-card-menu-btn" onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === aud.id ? null : aud.id); }}>
                    <FaEllipsisH />
                  </button>
                  {openMenuId === aud.id && (
                    <div className="aud-card-menu" onMouseDown={e => e.stopPropagation()}>
                      <button onClick={() => { setOpenMenuId(null); openEditModal(aud); }}>Edit</button>
                      <button onClick={() => { setOpenMenuId(null); handleCopy(aud); }}>Copy</button>
                      <button className="aud-menu-delete" onClick={() => { setOpenMenuId(null); handleDelete(aud.id); }}>Delete</button>
                    </div>
                  )}
                </div>
              </div>

              <div className="aud-card-tags">
                <span className="tag tag-gray">Ages {aud.ageMin}–{aud.ageMax}</span>
                <span className="tag tag-gray">{aud.gender || "All Genders"}</span>
                {(aud.locations || []).slice(0, 3).map(loc => (
                  <span key={typeof loc === "object" ? loc.key : loc} className="tag tag-blue">
                    {typeof loc === "object" ? loc.name : loc}
                  </span>
                ))}
                {(aud.locations || []).length > 3 && (
                  <span className="tag tag-gray">+{(aud.locations || []).length - 3} more</span>
                )}
                {(aud.interests || []).slice(0, 3).map(int => (
                  <span key={typeof int === "object" ? int.id : int} className="tag tag-purple">
                    {typeof int === "object" ? int.name : int}
                  </span>
                ))}
                {(aud.interests || []).length > 3 && (
                  <span className="tag tag-gray">+{(aud.interests || []).length - 3} interests</span>
                )}
              </div>

              <div className="aud-card-footer">
                {aud.createdAt && (
                  <span className="aud-card-date">
                    {new Date(aud.createdAt.seconds * 1000).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
